import React, { useContext, useEffect, useState } from "react";
import GlobalConfig from "../context/GlobalConfig";
import JourneyConfig from "../context/JourneyConfig";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import PropTypes from "prop-types";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { countries } from "../constants/countries";
import "./head.css";
// import continue_on_phone from "../assets/continue_on_phone.svg";
import useDeviceDetection from "../hooks/useDeviceDetection";
import { languages } from "../constants/languages";
import { languageData } from "../constants/languageData";

function Head({
  showBackIcon,
  handleBack,
  style,
  showCountry = true,
  showMobileFlow = true,
  showHelp = true,
  showLanguages = true,
  showMobileText = false,
  showTooltipOption = false,
  stepperFunc,
  countryCode,
}) {
  const { config, setConfig, routesAndNavigation } = useContext(GlobalConfig);
  const { isInvalidJourneyId } = useContext(JourneyConfig);
  const { userSettings } = config ?? {};
  let { branding } = config ?? {};
  if (!branding) {
    try {
      const brandingFromStorage = sessionStorage.getItem("branding");
      if (brandingFromStorage) {
        branding = JSON.parse(brandingFromStorage);
      }
    } catch (error) {
      console.error("Error parsing branding from sessionStorage:", error);
    }
  }
  // Screens that belong to one specific document pass that document's own country so the chip and
  // the on-screen instructions always name the same country. A screen that passes nothing, and a
  // document whose country could not be resolved, both fall back to the last country the applicant
  // picked — the same value the chip has always shown (IDX-677).
  const selectedCountryCode =
    countryCode ||
    branding?.countrySelected?.value ||
    sessionStorage.getItem("selectedCountry");
  const country = countries.find(
    (country) => country.value === selectedCountryCode
  );
  const isMobile = useDeviceDetection();
  const currentLanguage = sessionStorage.getItem("language") ?? "en";
  const [isVisible, setIsVisible] = useState(showTooltipOption);
  const color = config?.branding?.accentColor;
  const bgColor =
    "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";

  const language = languages;
  let sortedLang;
  // Header brand logo mirrors the footer. companyLogo is resolved by
  // one-journey-backend (Flow ▸ Team ▸ Org) — a single source of truth, rendered
  // directly and never hardcoded. When there is no logo the header shows nothing
  // (clean slate): no placeholder, no brand text.
  const brandLogo = branding?.companyLogo;
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => {
    setLogoFailed(false);
  }, [brandLogo]);
  const showBrandLogo = Boolean(brandLogo) && !logoFailed;

  useEffect(() => {
    if(showTooltipOption){
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 7000);

    return () => clearTimeout(timer);
  }
  }, []);

  const handleClick = () => {
    const enableMobileFlowKey = Object.keys(routesAndNavigation?.routes).find(
      (key) => routesAndNavigation?.routes[key].includes("enable-mobile-flow")
    );
    if (enableMobileFlowKey) {
      stepperFunc(enableMobileFlowKey);
    }
  };

  const handleCloseTooltip = (e) => {
    e.stopPropagation();
    setIsVisible(false);
  };

  if (language?.length)
    sortedLang = language?.sort((a, b) => a.label.localeCompare(b.label));
  if (isInvalidJourneyId) {
    return (
      <div
        className="fixed md:relative pt-3 pb-2 w-full flex flex-column justify-between items-center bg-white top-0 md:h-auto"
        style={style}
        data-testid="head"
      />
    );
  }
  return (
    <div
      data-testid="head"
      className="fixed md:relative pt-3 pb-2 w-full flex flex-column justify-between items-center bg-white top-0 md:h-auto h-[64px]"
      style={style}
    >
      <div className="flex items-center">
        {showBackIcon && (
          <div>
            <button
              data-testid="back-button"
              className={`px-[10px] ml-2 py-[10px] rounded-full cursor-pointer`}
              onClick={handleBack}
              style={{ color: color, backgroundColor: bgColor }}
              aria-label="Go back"
            >
              <ArrowLeftIcon className="md:h-5 h-4" style={{ color: color }} />
            </button>
          </div>
        )}
        <div className="ml-4">
          {showBrandLogo ? (
            <img
              src={brandLogo}
              alt="Logo"
              decoding="async"
              onError={() => setLogoFailed(true)}
              className="md:h-12 w-auto h-11"
            />
          ) : null}
        </div>
      </div>
      <div className="md:mr-6 mr-2  flex flex-row items-center md:gap-4 gap-2">
        {country && showCountry && selectedCountryCode && (
          <div
            className="hidden md:block md:font-[700px] "
            style={{ color: color }}
          >
            {country?.logo} {country?.label}
          </div>
        )}
        <div
          style={{
            backgroundColor: bgColor,
            borderRadius: 50,
            paddingLeft: 2,
            paddingRight: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {!showBackIcon && showLanguages && (
            <div style={{ paddingRight: 6 }}>
              <select
                value={currentLanguage}
                onChange={(e) =>{
                  sessionStorage.setItem(
                    "language",
                    e.target.value
                  );
                  setConfig({
                    ...config,
                    branding: { ...branding, language: e.target.value },
                  })
                }
                }
                className="block  px-[10px] py-[10px] h-[45px] rounded-md sm:text-sm font-sans"
                style={{
                  color: color,
                  borderRadius: "20px",
                  backgroundColor: bgColor,
                  fontWeight: 700,
                  fontSize: "12px",
                  maxWidth: "6rem",
                  maxHeight: "2.7rem",
                  border: "none",
                  outline: "none",
                }}
              >
                {(sortedLang ?? language)?.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {userSettings?.allowMobileSwitch && showMobileFlow && !isMobile && (
          <div className="relative inline-block">
          <button
            onClick={handleClick}
            className="md:px-[10px] md:py-[10px] px-[10px] py-[10px] md:gap-[6px] rounded-full"
            style={{
              backgroundColor: bgColor,
              color: color,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "2.7rem",
            }}
          >
            {showMobileText && (
              <span className="hidden md:block phone_text font-sans">
                Continue On Phone
              </span>
            )}
            <svg
              width="20"
              height="20"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="add_to_home_screen" clipPath="url(#clip0_2274_22553)">
                <path
                  id="Vector"
                  d="M12 0.673659L5.33333 0.666992C4.6 0.666992 4 1.26699 4 2.00033V4.00033H5.33333V3.33366H12V12.667H5.33333V12.0003H4V14.0003C4 14.7337 4.6 15.3337 5.33333 15.3337H12C12.7333 15.3337 13.3333 14.7337 13.3333 14.0003V2.00033C13.3333 1.26699 12.7333 0.673659 12 0.673659ZM6.66667 10.0003H8V5.33366H3.33333V6.66699H5.72667L2 10.3937L2.94 11.3337L6.66667 7.60699V10.0003Z"
                  fill={color ?? "#1658aa"}
                  style={{ fill: color ?? "#1658aa" }}
                />
              </g>
              <defs>
                <clipPath id="clip0_2274_22553">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
          {isVisible && (
            <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 font-inter">
              <div 
                className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 transform rotate-45 z-10"
                style={{ backgroundColor: color }}
              />
              <div 
                className="relative z-20 rounded-lg text-white"
                style={{ 
                  backgroundColor: color || '#1658aa',
                  width: '200px',
                  height: '105px',
                  padding: '16px'
                }}
              >
                <div className="flex justify-between items-center mb-1.5">
                  <p className="font-medium flex-grow text-left">{languageData[currentLanguage]?.header?.havingIssue ?? "Having issue?"}</p>
                  <button
                    onClick={handleCloseTooltip}
                    className="text-white hover:text-gray-200 ml-2"
                    aria-label="Close tooltip"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm opacity-90"
                style={{
                  color: '#FFF',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '16px',
                }}>
                  Switch to your phone for a clearer image capture
                </p>
              </div>
            </div>
          )}
        </div>
    
        )}
        {showHelp && userSettings?.displayHelp == true && (
          <div>
            <button
              className={`px-[10px] py-[10px] rounded-full cursor-pointer`}
              onClick={() => {
                const faqKey = Object.keys(routesAndNavigation?.routes).find((key) =>
                  routesAndNavigation?.routes[key].includes("faq")
                );
                if (faqKey) {
                  stepperFunc(faqKey);
                }
              }}
              style={{ backgroundColor: bgColor, color: color }}
              aria-label="Help"
            >
              <QuestionMarkCircleIcon
                className="h-5 "
                style={{ color: color }}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

Head.propTypes = {
  showBackIcon: PropTypes.bool,
  handleBack: PropTypes.func,
  countryCode: PropTypes.string,
};

Head.defaultValue = {
  showBackIcon: false,
};

export default Head;
